import { Button } from 'components/Button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/Dialog'
import { Check, ImageIcon, Loader2, Upload } from 'lucide-react'
import { apiCall } from 'modules/core/services/api'
import { CreativeService } from 'openapi/services/CreativeService'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useImageUpload } from '../../common/hooks/use-file-upload'

interface GeneratedImage {
  attachmentId: string
  imageUrl: string
  createdAt: string
  mimeType?: string
  ratio?: number
}

export interface PickerImageSelection {
  imageUrl: string
  imageAttachmentId: string
  source: 'generated' | 'uploaded'
  // Carried for downstream Instagram carousel preflight validation. Optional
  // because legacy synthetic entries (the parent's current single image) may
  // not have them — those selections will be flagged by the carousel validator
  // with a "Re-upload" message.
  mimeType?: string
  ratio?: number
}

interface ImagePickerModalBaseProps {
  creativeId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface SinglePickerProps extends ImagePickerModalBaseProps {
  mode?: 'single'
  currentImageUrl: string | null
  currentAttachmentId?: string | null
  onSelect: (image: PickerImageSelection) => void
  onSelectMulti?: never
  maxSelection?: never
  selectedAttachmentIds?: never
}

interface MultiPickerProps extends ImagePickerModalBaseProps {
  mode: 'multi'
  currentImageUrl?: string | null
  currentAttachmentId?: string | null
  onSelect?: never
  onSelectMulti: (images: PickerImageSelection[]) => void
  maxSelection: number
  selectedAttachmentIds?: string[]
}

type ImagePickerModalProps = SinglePickerProps | MultiPickerProps

const isMultiProps = (p: ImagePickerModalProps): p is MultiPickerProps => p.mode === 'multi'

export function ImagePickerModal(props: ImagePickerModalProps) {
  const { creativeId, currentImageUrl, currentAttachmentId, open, onOpenChange } = props
  const isMulti = isMultiProps(props)
  const maxSelection = isMulti ? props.maxSelection : 1
  const acceptMime = isMulti ? 'image/jpeg' : 'image/*'
  // Pull callbacks out so memoized handlers below don't depend on the whole props object.
  const onSelectSingle = isMulti ? undefined : props.onSelect
  const onSelectMulti = isMulti ? props.onSelectMulti : undefined
  const externalSelectedAttachmentIds = isMulti ? props.selectedAttachmentIds : undefined

  const [images, setImages] = useState<GeneratedImage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingSelection, setPendingSelection] = useState<PickerImageSelection[]>([])
  const [uploadWarning, setUploadWarning] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { handleUpload, isUploading } = useImageUpload({ maxSize: 10 * 1024 * 1024 })

  const loadImages = useCallback(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    apiCall(CreativeService.creativeControllerGetGeneratedImagesV1, null, creativeId)
      .then(data => {
        if (!cancelled) setImages(data.images || [])
      })
      .catch(err => {
        if (!cancelled) {
          setImages([])
          setError(err instanceof Error ? err.message : 'Failed to load images')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [creativeId])

  useEffect(() => {
    if (!open) return
    setPendingSelection([])
    setUploadWarning(null)
    return loadImages()
  }, [open, loadImages])

  const displayImages = useMemo(() => {
    if (!currentImageUrl) return images
    const alreadyInList = images.some(
      img => img.attachmentId === currentAttachmentId || img.imageUrl === currentImageUrl
    )
    if (alreadyInList) return images
    return [
      { attachmentId: currentAttachmentId ?? '', imageUrl: currentImageUrl, createdAt: '' },
      ...images,
    ]
  }, [images, currentImageUrl, currentAttachmentId])

  const togglePending = useCallback(
    (img: GeneratedImage) => {
      if (!isMulti) return
      setPendingSelection(prev => {
        const exists = prev.some(p => p.imageAttachmentId === img.attachmentId)
        if (exists) return prev.filter(p => p.imageAttachmentId !== img.attachmentId)
        if (prev.length >= maxSelection) return prev
        return [
          ...prev,
          {
            imageUrl: img.imageUrl,
            imageAttachmentId: img.attachmentId,
            source: 'generated',
            mimeType: img.mimeType,
            ratio: img.ratio,
          },
        ]
      })
    },
    [isMulti, maxSelection]
  )

  const handleImageClick = useCallback(
    (img: GeneratedImage) => {
      if (isMulti) {
        togglePending(img)
        return
      }
      onSelectSingle?.({
        imageUrl: img.imageUrl,
        imageAttachmentId: img.attachmentId,
        source: 'generated',
        mimeType: img.mimeType,
        ratio: img.ratio,
      })
      onOpenChange(false)
    },
    [isMulti, togglePending, onSelectSingle, onOpenChange]
  )

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return

      if (isMulti && file.type !== 'image/jpeg') {
        setUploadWarning('Instagram carousels require JPEG images.')
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }
      setUploadWarning(null)

      try {
        const attachment = await handleUpload(file)
        const selection: PickerImageSelection = {
          imageUrl: attachment.publicUrl,
          imageAttachmentId: attachment.id,
          source: 'uploaded',
          mimeType: attachment.mimeType,
          ratio: attachment.ratio,
        }
        if (isMulti) {
          setPendingSelection(prev => (prev.length >= maxSelection ? prev : [...prev, selection]))
        } else {
          onSelectSingle?.(selection)
          onOpenChange(false)
        }
      } catch (err) {
        // useImageUpload surfaces the user-facing error itself; log so the
        // console bridge fires for diagnostics on the underlying failure.
        console.error('ImagePickerModal: upload selection failed:', err)
      }

      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [handleUpload, onSelectSingle, onOpenChange, isMulti, maxSelection]
  )

  const isSelected = (img: GeneratedImage) => {
    if (isMulti) {
      const externallySelected = externalSelectedAttachmentIds?.includes(img.attachmentId)
      return (
        externallySelected || pendingSelection.some(p => p.imageAttachmentId === img.attachmentId)
      )
    }
    if (currentAttachmentId) return img.attachmentId === currentAttachmentId
    if (currentImageUrl) return img.imageUrl === currentImageUrl
    return false
  }

  const isExternallyLocked = (img: GeneratedImage) =>
    isMulti && externalSelectedAttachmentIds?.includes(img.attachmentId) === true

  const handleConfirmMulti = () => {
    if (!isMulti || pendingSelection.length === 0 || !onSelectMulti) return
    onSelectMulti(pendingSelection)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        withCloseButton
        className="max-md:top-auto max-md:right-3 max-md:bottom-3 max-md:left-3 max-md:w-auto max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle>{isMulti ? 'Add carousel images' : 'Choose Image'}</DialogTitle>
        </DialogHeader>

        <div className="mt-2 max-h-[60vh] overflow-y-auto">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || (isMulti && pendingSelection.length >= maxSelection)}
            className="mb-4 flex w-full items-center justify-center gap-2 rounded-[10px] border-2 border-dashed border-line bg-bg-2 px-4 py-5 text-[13px] font-medium text-paper-faint transition-colors hover:border-acid hover:bg-[color:var(--acid-tint)] hover:text-acid disabled:opacity-50"
          >
            {isUploading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={18} />
                Upload from device
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptMime}
            onChange={handleFileChange}
            className="hidden"
          />
          {uploadWarning && (
            <p className="mb-3 text-[12px] text-[color:var(--alert-error-text)]">{uploadWarning}</p>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-paper-faint" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <ImageIcon size={32} className="text-paper-faint" />
              <p className="text-[13px] text-paper-dim">{error}</p>
              <button
                type="button"
                onClick={loadImages}
                className="mt-1 text-[13px] font-medium text-acid hover:underline"
              >
                Try again
              </button>
            </div>
          ) : displayImages.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-12 text-center">
              <ImageIcon size={32} className="text-paper-faint" />
              <p className="text-[13px] text-paper-dim">No images created yet.</p>
              <p className="text-[12px] text-paper-faint">
                Use the chat to create images, or upload your own.
              </p>
            </div>
          ) : (
            <>
              <p className="mono mb-2 text-[10px] tracking-[0.2em] text-paper-faint uppercase">
                Created images
              </p>
              <div className="grid grid-cols-3 gap-3">
                {displayImages.map(img => {
                  const selected = isSelected(img)
                  const locked = isExternallyLocked(img)
                  return (
                    <button
                      key={img.attachmentId || img.imageUrl}
                      type="button"
                      onClick={() => !locked && handleImageClick(img)}
                      disabled={locked}
                      className={`group relative overflow-hidden rounded-[8px] border-2 transition-all ${
                        selected
                          ? 'border-acid shadow-[0_0_0_3px_var(--acid-soft)]'
                          : 'border-line hover:border-line-bright'
                      } ${locked ? 'cursor-not-allowed opacity-60' : ''}`}
                    >
                      <img src={img.imageUrl} alt="" className="aspect-video w-full object-cover" />
                      {selected && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <div className="rounded-full bg-acid p-1.5">
                            <Check size={14} className="text-ink-on-acid" />
                          </div>
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {isMulti && (
          <div className="mt-3 flex items-center justify-between border-t border-line pt-3">
            <span className="mono text-[10px] tracking-[0.15em] text-paper-dim uppercase">
              {pendingSelection.length} selected · {maxSelection} max
            </span>
            <Button
              type="button"
              variant="t3-primary"
              size="t3-sm"
              disabled={pendingSelection.length === 0}
              onClick={handleConfirmMulti}
            >
              Add {pendingSelection.length || ''} selected
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
